'use client';

import { AnimatePresence, m, useReducedMotion, type Variants } from 'motion/react';
import type { ReactNode } from 'react';

/** Ease-out-cubic — design system animations guide */
export const PREVIEW_PANEL_VISUAL_EASE_OUT_CUBIC = [0.215, 0.61, 0.355, 1] as const;

export const PREVIEW_PANEL_VISUAL_ENTER_DELAY_S = 0.14;
export const PREVIEW_PANEL_VISUAL_ENTER_DURATION_S = 0.28;

const EASE_OUT_CUBIC = PREVIEW_PANEL_VISUAL_EASE_OUT_CUBIC;
const ENTER_DELAY_S = PREVIEW_PANEL_VISUAL_ENTER_DELAY_S;
const ENTER_DURATION_S = PREVIEW_PANEL_VISUAL_ENTER_DURATION_S;
const EXIT_DURATION_S = 0.2;

/** Stagger in-step UI (e.g. mic preview dropdown) after primary panel enter finishes */
export const PREVIEW_PANEL_VISUAL_STAGGER_DELAY_S =
  PREVIEW_PANEL_VISUAL_ENTER_DELAY_S + PREVIEW_PANEL_VISUAL_ENTER_DURATION_S;

type PreviewPanelVisualTransitionProps = {
  visualKey: string;
  children: ReactNode;
  className?: string;
};

function buildVariants(prefersReducedMotion: boolean | null): Variants {
  if (prefersReducedMotion) {
    return {
      initial: { opacity: 1, y: 0 },
      animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.01 },
      },
      exit: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.01 },
      },
    };
  }

  return {
    initial: { opacity: 0, y: 12 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: ENTER_DURATION_S,
        ease: EASE_OUT_CUBIC,
        delay: ENTER_DELAY_S,
      },
    },
    exit: {
      opacity: 0,
      y: -8,
      transition: {
        duration: EXIT_DURATION_S,
        ease: EASE_OUT_CUBIC,
      },
    },
  };
}

export function PreviewPanelVisualTransition({
  visualKey,
  children,
  className,
}: PreviewPanelVisualTransitionProps) {
  const prefersReducedMotion = useReducedMotion();
  const variants = buildVariants(prefersReducedMotion);

  return (
    <AnimatePresence mode="wait">
      <m.div
        key={visualKey}
        role="presentation"
        className={className ?? 'relative size-full'}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {children}
      </m.div>
    </AnimatePresence>
  );
}
